import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Package, Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { STATUS_COLOURS, CATEGORY_LABEL, fmt } from './constants';

const ReceiveTab = ({
  receiveForm, setReceiveForm,
  receiveLoading, photoUploading,
  onSubmit, onUploadPhoto,
  onOpenAddExpected, onOpenEditExpected, onDeleteExpected,
  pieces, pieceSearch, setPieceSearch,
  pieceStatusFilter, setPieceStatusFilter,
  onRefreshPieces,
  isAdmin, onDeletePiece,
}) => (
  <TabsContent value="receive" className="mt-3">
    <form
      onSubmit={onSubmit}
      className="bg-white rounded-xl shadow p-4 space-y-3 border border-amber-100"
      data-testid="receive-form"
    >
      <h2 className="text-sm font-semibold text-amber-900">Receive New Piece</h2>
      <div>
        <Label>Branch</Label>
        <Input
          value={receiveForm.branch}
          onChange={(e) => setReceiveForm({ ...receiveForm, branch: e.target.value })}
          placeholder="e.g. Main Road"
          data-testid="receive-branch-input"
          className="h-11"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Gross Wt (gm)</Label>
          <Input
            type="number" inputMode="decimal" step="0.001" min="0"
            value={receiveForm.gross_weight}
            onChange={(e) => setReceiveForm({ ...receiveForm, gross_weight: e.target.value })}
            data-testid="receive-gross-input"
            className="h-11"
          />
        </div>
        <div>
          <Label>Purity (%)</Label>
          <Input
            type="number" inputMode="decimal" step="0.01" min="0" max="100"
            value={receiveForm.expected_purity}
            onChange={(e) => setReceiveForm({ ...receiveForm, expected_purity: e.target.value })}
            data-testid="receive-purity-input"
            className="h-11"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Expected stones</Label>
          <Button
            type="button" size="sm" onClick={onOpenAddExpected}
            className="h-8 bg-amber-700 hover:bg-amber-800"
            data-testid="receive-add-expected-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <div className="border rounded-lg divide-y" data-testid="receive-expected-list">
          {(receiveForm.expected_stone_entries || []).length === 0 && (
            <div className="text-center py-3 text-slate-400 text-xs">
              No stones expected · plain gold piece
            </div>
          )}
          {(receiveForm.expected_stone_entries || []).map((e, i) => {
            const isWeightOnlyEntry = e.category === 'dust' || e.category === 'others';
            return (
              <div key={i} className="p-2.5 flex items-center gap-2 text-sm" data-testid={`receive-expected-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{CATEGORY_LABEL[e.category] || e.category}</div>
                  <div className="text-xs text-slate-500">
                    {isWeightOnlyEntry ? `${fmt(e.wt)} gm` : `${e.pcs} pcs · ${fmt(e.wt)} gm`}
                  </div>
                </div>
                <Button type="button" size="icon" variant="ghost" onClick={() => onOpenEditExpected(i)} data-testid={`receive-expected-edit-${i}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" onClick={() => onDeleteExpected(i)} data-testid={`receive-expected-delete-${i}`}>
                  <Trash2 className="w-4 h-4 text-rose-600" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs">Photo</Label>
        {receiveForm.photo_url ? (
          <div className="mt-1 relative w-full h-40 rounded-lg overflow-hidden border" data-testid="receive-photo-preview">
            <img src={receiveForm.photo_url} alt="piece" className="w-full h-full object-cover" />
            <Button
              size="icon" variant="destructive"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => setReceiveForm({ ...receiveForm, photo_url: '' })}
              data-testid="receive-photo-remove"
              type="button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="mt-1 flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 text-amber-800 cursor-pointer text-sm">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => onUploadPhoto(e.target.files?.[0])}
              data-testid="receive-photo-input"
            />
            {photoUploading ? 'Uploading…' : (<><Package className="w-4 h-4" /> Snap or upload photo</>)}
          </label>
        )}
      </div>
      <div>
        <Label>Remarks</Label>
        <Textarea
          rows={2}
          value={receiveForm.remarks}
          onChange={(e) => setReceiveForm({ ...receiveForm, remarks: e.target.value })}
          data-testid="receive-remarks-input"
        />
      </div>
      <Button type="submit" disabled={receiveLoading || photoUploading} className="w-full h-12 bg-amber-700 hover:bg-amber-800" data-testid="receive-submit-btn">
        <Plus className="w-4 h-4 mr-2" /> {receiveLoading ? 'Saving…' : 'Receive Piece'}
      </Button>
    </form>

    <div className="mt-4 bg-white rounded-xl shadow border border-amber-100">
      <div className="flex flex-wrap gap-2 items-center p-3 border-b">
        <Input
          placeholder="Search serial…"
          value={pieceSearch}
          onChange={(e) => setPieceSearch(e.target.value)}
          data-testid="piece-search-input"
          className="flex-1 min-w-[140px] h-10"
        />
        <Select value={pieceStatusFilter} onValueChange={setPieceStatusFilter}>
          <SelectTrigger className="w-32 h-10" data-testid="piece-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="broken">Broken</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={onRefreshPieces} className="h-10 w-10" data-testid="piece-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="divide-y" data-testid="pieces-table-body">
        {pieces.length === 0 && (
          <div className="text-center py-6 text-slate-400 text-sm">No pieces yet</div>
        )}
        {pieces.map((p) => (
          <div key={p._id} className="p-3 flex items-center gap-3" data-testid={`piece-row-${p.serial_no}`}>
            {p.photo_url ? (
              <img src={p.photo_url} alt={p.serial_no} className="w-12 h-12 rounded object-cover border" />
            ) : (
              <div className="w-12 h-12 rounded bg-slate-100 border grid place-items-center text-slate-300">
                <Package className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{p.serial_no}</span>
                <Badge className={STATUS_COLOURS[p.status] || ''}>{p.status}</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">
                {p.branch} · {fmt(p.gross_weight)} gm · {fmt(p.expected_purity, 2)}%
                {p.lot_no && <> · <span className="font-mono">{p.lot_no}</span></>}
              </div>
              {p.expected_stones && (p.expected_stones.diamond + p.expected_stones.stone + p.expected_stones.beads_pearl) > 0 && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Expected:
                  {p.expected_stones.diamond > 0 && <> 💎{p.expected_stones.diamond}</>}
                  {p.expected_stones.stone > 0 && <> ◆{p.expected_stones.stone}</>}
                  {p.expected_stones.beads_pearl > 0 && <> ◯{p.expected_stones.beads_pearl}</>}
                </div>
              )}
            </div>
            {isAdmin && p.status === 'in_stock' && (
              <Button
                size="icon" variant="ghost"
                onClick={() => onDeletePiece(p)}
                data-testid={`piece-delete-${p.serial_no}`}
                title="Delete piece (admin)"
              >
                <Trash2 className="w-4 h-4 text-rose-600" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  </TabsContent>
);

export default ReceiveTab;
